import { supabase } from '@/integrations/supabase/client';
import { getUserGymId } from '@/utils/gymAuth';
import { invokeWithRetry } from './common/invokeWithRetry';
import { isTransportFailure, isLocalWriteFallbackAllowed } from './common/isTransportFailure';

/**
 * Rappel sur `courses-management` (cf. `supabase/functions/courses-management/index.ts`) :
 * elle renvoie 403 pour les refus de rôle (« Unauthorized: Admin role required »)
 * et 400 pour ses gardes métier (« Cannot delete course: scheduled classes
 * exist », « Cannot delete category: courses exist », « Course not found in this
 * gym »). Les deux sont des décisions du serveur : `isTransportFailure` les
 * classe en refus, et le repli en écriture directe n'est plus déclenché.
 *
 * Les LECTURES (`getCourses`, `getCategories`) utilisent `isTransportFailure` ;
 * les ÉCRITURES utilisent `isLocalWriteFallbackAllowed`, qui refuse en plus le
 * repli sur un dépassement du délai client. C'est indispensable ici :
 * `createCourse` et `createCategory` n'ont AUCUNE garde d'unicité, ni côté
 * serveur ni côté repli. Un insert direct déclenché parce que la fonction edge
 * était simplement lente (et non injoignable) créerait donc un doublon franc.
 */

// ✅ Feature flags for progressive activation
const FEATURE_FLAGS = {
  USE_EDGE_FUNCTION_GET_COURSES: true,
  USE_EDGE_FUNCTION_GET_CATEGORIES: true,
  USE_EDGE_FUNCTION_CREATE_COURSE: true,
  USE_EDGE_FUNCTION_UPDATE_COURSE: true,
  USE_EDGE_FUNCTION_DELETE_COURSE: true,
  USE_EDGE_FUNCTION_CREATE_CATEGORY: true,
  USE_EDGE_FUNCTION_UPDATE_CATEGORY: true,
  USE_EDGE_FUNCTION_DELETE_CATEGORY: true,
};

/**
 * Wrapper for courses-management edge function: get_courses action
 * Centralizes all courses fetching with gym_id validation
 */
export async function getCourses(gymId?: string): Promise<any[]> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for getCourses');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_GET_COURSES) {
    console.log('[coursesWrapper] Using edge function for getCourses');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('courses-management', {
      action: 'get_courses',
      gym_id: targetGymId,
    });

    if (authExpired) {
      // Déconnexion et redirection déjà déclenchées par `invokeWithRetry`.
      return [];
    }

    if (error) {
      // Un 403 (rôle manquant) ou un 400 (« does not have access to this gym »)
      // est une décision du serveur : la relire en direct depuis le navigateur
      // la contournerait. On propage.
      if (!isTransportFailure(error, { authExpired, forbidden })) {
        console.error('[coursesWrapper] getCourses refused by server, no fallback:', error);
        throw error;
      }
      console.error('[coursesWrapper] Edge function unreachable, falling back to direct query:', error);
    } else {
      return data?.courses || [];
    }
  }

  // Fallback: direct database query
  // `course_calendars` est joint pour exposer `calendar_ids` comme la fonction
  // edge : sans lui, un cours édité pendant une panne edge perdrait ses
  // affectations de plannings (le dialogue les croirait vides).
  console.log('[coursesWrapper] Using direct query for getCourses (fallback)');
  const { data, error } = await supabase
    .from('courses')
    .select(`
      *,
      category:categories(id, name),
      course_calendars(calendar_id)
    `)
    .eq('gym_id', targetGymId)
    .order('name');

  if (error) throw error;
  return (data || []).map(({ course_calendars, ...course }: any) => ({
    ...course,
    calendar_ids: (course_calendars || []).map((l: any) => l.calendar_id),
  }));
}

/**
 * Wrapper for courses-management edge function: get_categories action
 * Centralizes all categories fetching with gym_id validation
 */
export async function getCategories(gymId?: string): Promise<any[]> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for getCategories');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_GET_CATEGORIES) {
    console.log('[coursesWrapper] Using edge function for getCategories');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('courses-management', {
      action: 'get_categories',
      gym_id: targetGymId,
    });

    if (authExpired) {
      return [];
    }

    if (error) {
      // Idem getCourses : pas de lecture directe sur un refus du serveur.
      if (!isTransportFailure(error, { authExpired, forbidden })) {
        console.error('[coursesWrapper] getCategories refused by server, no fallback:', error);
        throw error;
      }
      console.error('[coursesWrapper] Edge function unreachable, falling back to direct query:', error);
    } else {
      return data?.categories || [];
    }
  }

  // Fallback: direct database query
  console.log('[coursesWrapper] Using direct query for getCategories (fallback)');
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('gym_id', targetGymId)
    .order('name');

  if (error) throw error;
  return data || [];
}

/**
 * Wrapper for courses-management edge function: create_course action
 * Centralizes course creation with permission validation
 */
export async function createCourse(courseData: {
  name: string;
  description?: string;
  category_id?: string;
  default_capacity?: number;
  default_duration?: number;
  gym_id?: string;
  /** Plannings affectés. Vide/absent = cours « universel » (tous les plannings). */
  calendar_ids?: string[];
}): Promise<any> {
  const targetGymId = courseData.gym_id || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for createCourse');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_CREATE_COURSE) {
    console.log('[coursesWrapper] Using edge function for createCourse');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('courses-management', {
      action: 'create_course',
      gym_id: targetGymId,
      ...courseData,
    });

    if (authExpired) {
      throw new Error('Session expired');
    }

    if (error) {
      // Refus de rôle (403 « Admin or Coach role required ») ou validation
      // serveur (400 « category_id is required ») : l'insert direct les
      // contournait. On propage désormais le message du serveur, affichable tel
      // quel par `pages/Classes.tsx`.
      if (!isLocalWriteFallbackAllowed(error, { authExpired, forbidden })) {
        console.error('[coursesWrapper] createCourse refused by server, no direct insert:', error);
        throw error;
      }
      console.error('[coursesWrapper] Edge function unreachable, falling back to direct insert:', error);
    } else {
      return data?.course;
    }
  }

  // Fallback: direct database insert
  console.log('[coursesWrapper] Using direct insert for createCourse (fallback)');
  const { data, error } = await supabase
    .from('courses')
    .insert({
      name: courseData.name,
      description: courseData.description,
      category_id: courseData.category_id,
      default_capacity: courseData.default_capacity || 10,
      default_duration: courseData.default_duration || 60,
      gym_id: targetGymId
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Wrapper for courses-management edge function: update_course action
 * Centralizes course updates with permission validation
 */
export async function updateCourse(
  courseId: string,
  updates: Partial<{
    name: string;
    description: string;
    category_id: string;
    default_capacity: number;
    default_duration: number;
  }>,
  gymId?: string,
  /** Plannings affectés. `undefined` = ne pas toucher ; `[]` = universel. */
  calendarIds?: string[]
): Promise<any> {
  const targetGymId = gymId || getUserGymId();

  if (!targetGymId) {
    throw new Error('gym_id is required for updateCourse');
  }

  let edgeFunctionError: any = null;
  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_UPDATE_COURSE) {
    console.log('[coursesWrapper] Using edge function for updateCourse');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('courses-management', {
      action: 'update_course',
      gym_id: targetGymId,
      courseId,
      updates,
      ...(calendarIds !== undefined ? { calendar_ids: calendarIds } : {}),
    });

    if (authExpired) {
      throw new Error('Session expired');
    }

    if (error) {
      // « Course not found in this gym » et le refus de rôle sont des décisions
      // du serveur : l'update direct ne doit plus servir de deuxième chance.
      if (!isLocalWriteFallbackAllowed(error, { authExpired, forbidden })) {
        console.error('[coursesWrapper] updateCourse refused by server, no direct update:', error);
        throw error;
      }
      console.error('[coursesWrapper] Edge function unreachable, falling back to direct update:', error);
      edgeFunctionError = error;
    } else {
      return data?.course;
    }
  }

  // Fallback: direct database update
  // `.eq('gym_id')` : cloisonnement multi-tenant.
  // `.select()` sans `.single()` : on veut distinguer « 0 ligne » (RLS ou
  // mauvais tenant, qui ne produit AUCUNE erreur) d'un vrai succès.
  console.log('[coursesWrapper] Using direct update for updateCourse (fallback)');
  const { data, error } = await supabase
    .from('courses')
    .update(updates)
    .eq('id', courseId)
    .eq('gym_id', targetGymId)
    .select();

  if (error) {
    console.error('[coursesWrapper] Fallback also failed:', error);
    throw new Error(edgeFunctionError?.message || error.message || 'Failed to update course');
  }
  if (!data || data.length === 0) {
    throw new Error(
      edgeFunctionError?.message ||
        `Course ${courseId} not updated: not found in gym ${targetGymId} or blocked by row-level security`
    );
  }
  return data[0];
}

/**
 * Wrapper for courses-management edge function: delete_course action
 * Centralizes course deletion with permission and dependency validation
 */
export async function deleteCourse(courseId: string, gymId?: string): Promise<void> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for deleteCourse');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_DELETE_COURSE) {
    console.log('[coursesWrapper] Using edge function for deleteCourse');
    const { error, authExpired, forbidden } = await invokeWithRetry('courses-management', {
      action: 'delete_course',
      gym_id: targetGymId,
      courseId,
    });

    if (authExpired) {
      throw new Error('Session expired');
    }

    if (error) {
      // Cas emblématique de l'audit 0.7 : la fonction edge refuse en 400
      // « Cannot delete course: scheduled classes exist » et en 403
      // « Unauthorized: Admin role required ». L'ancien `catch` supprimait le
      // cours en direct malgré ce refus. On propage désormais.
      if (!isLocalWriteFallbackAllowed(error, { authExpired, forbidden })) {
        console.error('[coursesWrapper] deleteCourse refused by server, no direct delete:', error);
        throw error;
      }
      console.error('[coursesWrapper] Edge function unreachable, falling back to direct delete:', error);
    } else {
      return;
    }
  }

  // Fallback: direct database delete
  // Atteint uniquement si la fonction edge est injoignable. On rejoue donc sa
  // garde de dépendance : sinon une coupure réseau suffirait à supprimer un
  // cours encore référencé par des séances programmées.
  console.log('[coursesWrapper] Using direct delete for deleteCourse (fallback)');

  const { data: scheduledClasses, error: dependencyError } = await supabase
    .from('scheduled_classes')
    .select('id')
    .eq('course_id', courseId)
    .eq('gym_id', targetGymId)
    .limit(1);

  if (dependencyError) throw dependencyError;
  if (scheduledClasses && scheduledClasses.length > 0) {
    throw new Error('Cannot delete course: scheduled classes exist');
  }

  // `.select()` est indispensable : un `delete()` bloqué par RLS supprime
  // 0 ligne et ne renvoie AUCUNE erreur. Sans cette vérification, l'UI affichait
  // un succès et le cours réapparaissait au rafraîchissement suivant.
  const { data: deleted, error } = await supabase
    .from('courses')
    .delete()
    .eq('id', courseId)
    .eq('gym_id', targetGymId)
    .select('id');

  if (error) throw error;
  if (!deleted || deleted.length === 0) {
    throw new Error(
      `Course ${courseId} not deleted: not found in gym ${targetGymId} or blocked by row-level security`
    );
  }
}

/**
 * Wrapper for courses-management edge function: create_category action
 * Centralizes category creation with permission validation
 */
export async function createCategory(name: string, gymId?: string): Promise<any> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for createCategory');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_CREATE_CATEGORY) {
    console.log('[coursesWrapper] Using edge function for createCategory');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('courses-management', {
      action: 'create_category',
      gym_id: targetGymId,
      name,
    });

    if (authExpired) {
      throw new Error('Session expired');
    }

    if (error) {
      // 403 « Unauthorized: Admin role required » : un coach ne doit pas pouvoir
      // créer une catégorie via le repli en insert direct.
      if (!isLocalWriteFallbackAllowed(error, { authExpired, forbidden })) {
        console.error('[coursesWrapper] createCategory refused by server, no direct insert:', error);
        throw error;
      }
      console.error('[coursesWrapper] Edge function unreachable, falling back to direct insert:', error);
    } else {
      return data?.category;
    }
  }

  // Fallback: direct database insert
  console.log('[coursesWrapper] Using direct insert for createCategory (fallback)');
  const { data, error } = await supabase
    .from('categories')
    .insert({ name, gym_id: targetGymId })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Wrapper for courses-management edge function: update_category action
 * Centralizes category updates with permission validation
 */
export async function updateCategory(categoryId: string, name: string, gymId?: string): Promise<any> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for updateCategory');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_UPDATE_CATEGORY) {
    console.log('[coursesWrapper] Using edge function for updateCategory');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('courses-management', {
      action: 'update_category',
      gym_id: targetGymId,
      categoryId,
      name,
    });

    if (authExpired) {
      throw new Error('Session expired');
    }

    if (error) {
      // 403 « Unauthorized: Admin role required » : pas de contournement par
      // update direct.
      if (!isLocalWriteFallbackAllowed(error, { authExpired, forbidden })) {
        console.error('[coursesWrapper] updateCategory refused by server, no direct update:', error);
        throw error;
      }
      console.error('[coursesWrapper] Edge function unreachable, falling back to direct update:', error);
    } else {
      return data?.category;
    }
  }

  // Fallback: direct database update
  // `.eq('gym_id')` : cloisonnement multi-tenant. `.select()` sans `.single()` :
  // 0 ligne (RLS ou mauvais tenant) ne remonte aucune erreur, on le détecte donc
  // explicitement au lieu d'afficher un faux succès.
  console.log('[coursesWrapper] Using direct update for updateCategory (fallback)');
  const { data, error } = await supabase
    .from('categories')
    .update({ name })
    .eq('id', categoryId)
    .eq('gym_id', targetGymId)
    .select();

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(
      `Category ${categoryId} not updated: not found in gym ${targetGymId} or blocked by row-level security`
    );
  }
  return data[0];
}

/**
 * Wrapper for courses-management edge function: delete_category action
 * Centralizes category deletion with permission and dependency validation
 */
export async function deleteCategory(categoryId: string, gymId?: string): Promise<void> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for deleteCategory');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_DELETE_CATEGORY) {
    console.log('[coursesWrapper] Using edge function for deleteCategory');
    const { error, authExpired, forbidden } = await invokeWithRetry('courses-management', {
      action: 'delete_category',
      gym_id: targetGymId,
      categoryId,
    });

    if (authExpired) {
      throw new Error('Session expired');
    }

    if (error) {
      // 400 « Cannot delete category: courses exist » et 403 « Admin role
      // required » : décisions du serveur, plus de suppression directe.
      if (!isLocalWriteFallbackAllowed(error, { authExpired, forbidden })) {
        console.error('[coursesWrapper] deleteCategory refused by server, no direct delete:', error);
        throw error;
      }
      console.error('[coursesWrapper] Edge function unreachable, falling back to direct delete:', error);
    } else {
      return;
    }
  }

  // Fallback: direct database delete
  // Garde de dépendance rejouée localement (voir deleteCourse) : une coupure
  // réseau ne doit pas permettre de supprimer une catégorie encore utilisée.
  console.log('[coursesWrapper] Using direct delete for deleteCategory (fallback)');

  const { data: linkedCourses, error: dependencyError } = await supabase
    .from('courses')
    .select('id')
    .eq('category_id', categoryId)
    .eq('gym_id', targetGymId)
    .limit(1);

  if (dependencyError) throw dependencyError;
  if (linkedCourses && linkedCourses.length > 0) {
    throw new Error('Cannot delete category: courses exist');
  }

  // `.select()` obligatoire : un delete bloqué par RLS renvoie 0 ligne sans
  // erreur, ce qui se traduirait par un succès mensonger dans l'UI.
  const { data: deleted, error } = await supabase
    .from('categories')
    .delete()
    .eq('id', categoryId)
    .eq('gym_id', targetGymId)
    .select('id');

  if (error) throw error;
  if (!deleted || deleted.length === 0) {
    throw new Error(
      `Category ${categoryId} not deleted: not found in gym ${targetGymId} or blocked by row-level security`
    );
  }
}
